/* eslint-disable @next/next/no-img-element */
import { PlusIcon } from '@heroicons/react/outline';
import { Button } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import KeyIcon from 'assets/Icons/KeyIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import mixpanel from 'mixpanel-browser';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import RenderIf from '@/components/Common/RenderIf';

import { cartAtom } from '@/atoms/Cart';
import { getTrackOrderLink } from '@/utils';
import { formatDateByCountry } from '@/utils/FormatDateByCountry';
import { formatPrice } from '@/utils/Product';

import { usePayment } from '../../CartPage/RazorPay';
import { Category, formatItem, Type } from '../../CartPage/Step_3_orderCard';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import { BookDummy } from '../../../../assets';
import RetryIcon from '../../../../assets/Icons/RetryIcon';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { handleConvertToCod } from '../../../services/Cart';
import { syncOrder, syncOrderInApp } from '../../../services/Orders';
import { FormattedOrder } from '../../../utils/interfaces/Orders';
import { User } from '../../../utils/interfaces/User';

interface Props {
  data: FormattedOrder;
  setFetchOrders: (value: boolean) => void;
  fetchOrders: boolean;
  isInAppPurchase?: boolean;
}

enum Status {
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  IN_TRANSIT = 'in transit',
  DELIVERED = 'delivered',
  CANCELED = 'canceled',
  FAILED = 'failed',
  REFUNDED = 'refunded',
  OUT_FOR_DELIVERY = 'out for delivery',
}

const MyOrderCard: React.FC<Props> = ({
  data,
  fetchOrders,
  setFetchOrders,
  isInAppPurchase,
}) => {
  const [isRotating, setIsRotating] = useState(false);

  const [convertCodLoading, setConvertCodLoading] = useState(false);
  const userData: User | undefined = useRecoilValue(user);
  const { initializeRazorpay } = usePayment();
  const cartData = useRecoilValue(cartAtom);
  const screens = useBreakpoint();
  const router = useRouter();
  const environment = process.env.NEXT_PUBLIC_APP_ENV;
  const cod_limit =
    Number(process.env.NEXT_PUBLIC_ALLOWABLE_COD_AMOUNT) || 12000;

  const { cartId } = router.query;
  const handlePlaceOrder = async () => {
    try {
      await initializeRazorpay({
        totalAmount: Number(data?.grand_total) || 0,
        razorpayOrderId: data?.rzp_order_id as string,
        orderId: data?.orderId,
        userData: userData as User,
        setFetchOrders: setFetchOrders,
        fetchOrders: fetchOrders,
        paymentType: '',
        cartId: (cartData[0]?.cartId as number) || Number(cartId),
      });
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const convertToCod = async () => {
    try {
      setConvertCodLoading(true);
      await handleConvertToCod({
        order_id: data?.orderId as string,
      });

      const eventParams: EventDetails = {
        event_name: 'convert_cod',
        source: 'ecom',
        source_type: 'orders',
        source_id: data?.orderId?.toString() as string,
        sub_source: null,
        sub_source_id: null,
        unit: 'INR',
        currency: 'INR',
        value: data?.grand_total,
        meta: {
          click_source: 'retry_popup',
          payment_mode:
            data?.paymentType === 'Cash On Delivery' ? 'cod' : 'prepaid',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });

      if (typeof mixpanel !== 'undefined' && environment === 'production') {
        mixpanel.track(eventParams.event_name as string, eventParams);
      }

      if (setFetchOrders) {
        setFetchOrders(!fetchOrders);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setConvertCodLoading(false);
    }
  };

  const DotLoader = () => {
    return (
      <motion.span
        className='ml-1'
        initial={{ opacity: 0.2 }}
        animate={{ opacity: 1 }}
        transition={{
          repeat: Infinity,
          repeatType: 'mirror',
          duration: 1,
        }}
      >
        ...
      </motion.span>
    );
  };
  const handleSyncOrder = async () => {
    try {
      setIsRotating(true);
      if (isInAppPurchase) {
        await syncOrderInApp(data?.orderId);
      } else {
        await syncOrder(data?.orderId);
      }

      if (setFetchOrders) {
        setFetchOrders(!fetchOrders);
      }
    } catch (error: any) {
      const message =
        formateErrorObject(error) || 'An error occurred while fetching data.';

      notify(
        message === 'Too Many Attempts.'
          ? 'Please wait 10 minutes before trying again.'
          : message,
        'error',
      );
    } finally {
      setIsRotating(false);
    }
  };
  const containCodFalse = useMemo(() => {
    const hasNonDigital = data.list.some(
      (item) => item?.i18n_attributes?.cash_on_delivery === false,
    );
    return !hasNonDigital;
  }, [data]);

  useEffect(() => {

    if (
      (data.payment_status === 'Un-Paid' || data.payment_status === 'failed') &&
      data.paymentType?.toLowerCase() == 'razorpay'
    ) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }

    return () => { };
  }, [data.payment_status, data.paymentType]);

  // const isAllDigital = useMemo(() => {
  //   const hasNonDigital = data.list.some((item) => item.type !== 'digital');
  //   return !hasNonDigital;
  // }, [data.list]);

  // const isCanceled = useMemo(() => {
  //   const hasCanceled = data.list.some(
  //     (item) => item.status?.toLowerCase() !== Status.CANCELED,
  //   );
  //   return hasCanceled;
  // }, [data.list]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case Status.PROCESSING:
        return 'text-orange-400';
      case Status.SHIPPED:
        return 'text-blue-300';
      case Status.IN_TRANSIT:
        return 'text-orange-300';
      case Status.DELIVERED:
        return 'text-green-400';
      case Status.CANCELED:
        return 'text-red-500';
      case Status.FAILED:
        return 'text-red-600';
      case Status.REFUNDED:
        return 'text-orange-400';
      case Status.OUT_FOR_DELIVERY:
        return 'text-indigo-300';
      case 'Pending':
        return 'text-orange-400';
      default:
        return 'text-gray-100';
    }
  };

  const hasDigitalOrPreorder = data.list.some(
    (item) => item.type === 'digital' || item.is_preorder === true,
  );

  return (
    <div className='cardShadow p-4 md:p-8 flex flex-col bg-cartBgColor rounded-lg'>
      <div
        className={clsx(
          !isInAppPurchase && 'md:items-center',
          'flex gap-y-1 md:gap-y-2 md:flex-row flex-col  justify-between',
        )}
      >
        <div className='flex flex-col'>
          <span className='text-fadedWhite text-base md:text-xl gap-x-2 font-medium flex justify-between'>
            <p> Order ID </p>{' '}
            <p className='flex gap-x-2 items-center'> {data.orderId} </p>{' '}
          </span>
          <span className='text-sm text-[#626262] font-medium flex justify-between md:justify-normal gap-x-2 mt-5 md:mt-0  '>
            <p>Order date :</p>{' '}
            <p className=' font-normal text-fadedWhite'>
              {formatDateByCountry(
                data.orderDate as string,
                userData,
                'dateOnly',
              )}
            </p>
          </span>
        </div>

        <span>
          <span>
            <span className='text-sm text-textGray flex md:justify-end gap-x-1 font-medium'>
              <span className='inline-flex justify-between md:justify-normal text-[#626262] gap-x-2 w-full'>
                <p className='whitespace-nowrap'> Payment Status :</p>{' '}
                <p
                  className={clsx(
                    data.payment_status === 'Un-Paid' ||
                      data.payment_status === 'failed'
                      ? 'text-red-500'
                      : 'text-green-500',
                    'capitalize',
                  )}
                >
                  {data.payment_status}
                </p>
              </span>
            </span>
            <ShouldRender check={data.paymentType}>
              <span className='text-sm text-[#626262] font-medium flex justify-between  gap-x-1 mt-1'>
                <p className=''>Payment Type :</p>{' '}
                <p className='text-fa text-fadedWhite font-normal'>
                  {data.paymentType}
                </p>
              </span>
            </ShouldRender>
          </span>
        </span>
      </div>
      <div
        style={{
          border: '1px solid #FFFFFF33',
        }}
        className=' my-8'
      />
      <div>
        {data.list.map((items, index) => {
          const isArriving = items.status === 'In transit';
          return (
            <div key={`${items?.name}-${index}`}>
              {' '}
              <div className='flex md:flex-row flex-col gap-5'>
                <div className='   md:w-40 xs:h-full md:h-32 h-32 max-h-32 min-w-64 min-h-40 bg-black rounded-lg flex justify-center md:gap-x-1.5 px-4 pt-5'>
                  <span
                    onClick={() => {
                      if (items?.type !== 'hardcover') {
                        data.payment_status?.toLowerCase() === 'paid' ||
                          data.payment_status?.toLowerCase() === 'completed'
                          ? router.push('/library')
                          : notify(
                            `The ${items?.itemType === 'Book' ? 'eBook' : items.itemType?.toLowerCase()} will be available once the payment is successfully completed.`,
                          );
                      }
                    }}
                    className={clsx(
                      items?.type !== 'hardcover' && 'cursor-pointer',
                      'w-28 md:w-fit',
                    )}
                  >
                    <Image
                      src={items?.image || BookDummy}
                      alt='Product'
                      width={screens.md ? 115 : 80}
                      height={170}
                      className='object-contain w-full h-full'
                    />
                  </span>
                  <ShouldRender
                    check={
                      items?.type === 'hardcover' &&
                      items?.itemType?.toUpperCase() === 'BOOK' &&
                      Boolean(items?.digital_pair)
                    }
                  >
                    <span
                      onClick={() => {
                        data.payment_status?.toLowerCase() === 'paid' ||
                          data.payment_status?.toLowerCase() === 'completed'
                          ? router.push('/library')
                          : notify(
                            `The ${items?.itemType === 'Book' ? 'eBook' : items.itemType?.toLowerCase()} will be available once the payment is successfully completed.`,
                          );
                      }}
                      className='flex justify-end items-end cursor-pointer relative '
                    >
                      <span className='px-1 pt-2 md:bg-[#434343] rounded-md'>
                        <Image
                          src={items?.image || BookDummy}
                          alt='Product'
                          width={80}
                          height={120}
                          className='object-contain h-[100px] md:w-[80px] md:h-[120px]'
                        />
                      </span>

                      <div className="absolute inset-0 before:content-[''] before:absolute before:inset-0 before:bg-gradient-to-t from-bgColor " />
                      <div className='absolute md:pl-2 pb-1 text-fadedWhite text-[12px] md:text-sm  leading-tight left-[10px] md:left-0 bottom-0.5'>
                        <p className='flex justify-center pb-1 md:pb-2 pr-2 items-center'>
                          <PlusIcon className='w-3 h-3 md:w-5 md:h-5' />
                        </p>{' '}
                        DIGITAL COPY
                      </div>
                    </span>
                  </ShouldRender>
                </div>
                <div className='grid  lg:grid-cols-2   md:gap-y-5 w-full text-fadedWhite mt-4 md:mt-0 md:py-4'>
                  <span className='flex flex-col lg:justify-between gap-y-2 h-full md:h-auto lg:col-span-1 '>
                    <div className='flex flex-col'>
                      <span className='text-xs md:text-sm  text-textGrayColor'>
                        {formatItem(
                          items?.itemType as Category,
                          items?.category as Type,
                        )?.toUpperCase()}
                      </span>
                      <span className=' text-base md:text-xl font-semibold'>
                        {items.name}
                      </span>
                    </div>

                    <span className='hidden md:flex gap-x-2 text-ba md:text-xl items-end'>
                      {data.paymentType?.toLowerCase() == 'subscription' ? (
                        <div className='flex items-center gap-1'>
                          <b>1</b>
                          <KeyIcon />
                        </div>
                      ) : (
                        <p className='font-bold'>
                          {' '}
                          {items.currency_symbol}
                          {formatPrice(items.price)}
                        </p>
                      )}

                      <p className='line-through font-thin text-base'>
                        {items.currency_symbol}
                        {formatPrice(items.actualPrice)}
                      </p>
                    </span>
                  </span>
                  <div className='flex justify-between items-start pt-5 md:pt-0'>
                    <span className='lg:text-center text-sm md:text-base lg:text-xl  w-full'>
                      <span className='md:hidden flex gap-x-2 text-xl items-center'>
                        {data.paymentType === 'Subscription' ? (
                          <div className='flex items-center gap-2'>
                            <b>01</b>
                            <KeyIcon />
                          </div>
                        ) : (
                          <p className='font-bold'>
                            {' '}
                            {items.currency_symbol}
                            {formatPrice(items.price)}
                          </p>
                        )}

                        <p className='line-through text-base'>
                          {items.currency_symbol}
                          {items.actualPrice}
                        </p>
                      </span>
                      <span className='text-xs md:text-base'>
                        Qty. {items.quantity}
                      </span>
                    </span>
                    <span
                      className={clsx(
                        isArriving && 'text-green-600',
                        'flex flex-col jus font-medium gap-y-2  w-full text-base md:text-lg',
                      )}
                    >
                      {' '}
                      {/* <ShouldRender
                        check={items.type !== 'digital' || isInAppPurchase}
                      > */}
                      <span className='flex flex-col gap-y-2'>
                        <span className='flex justify-end font-thin text-sm whitespace-nowrap '>
                          Delivery Status
                        </span>

                        <span
                          className={`flex  text-base justify-end capitalize ${getStatusColor(
                            items.status?.toLowerCase() as Status,
                          )}`}
                        >
                          {items.status}
                        </span>
                      </span>
                      {/* </ShouldRender> */}
                      <span className='text-right'>
                        {isArriving && 'Arriving'} {items.statusDate}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <ShouldRender check={index !== data.list.length - 1}>
                <div className='py-2 md:py-4'>
                  <div className='border-b border-black ' />
                </div>
              </ShouldRender>
            </div>
          );
        })}
      </div>

      <div className='flex md:flex-row flex-col-reverse gap-2 mt-4 lg:mt-8 md:gap-4 justify-between'>
        <span className=' flex justify-start md:justify-center  md:min-w-64'>
          <ShouldRender check={data.payment_status === 'Paid' && screens.md}>
            <div>
              <Link className='w-full' href='/library'>
                <Button
                  size='middle'
                  className='border w-full border-[#363636] h-9'
                >
                  <span className='px-1 md:px-4 w-full text-sm  text-fadedWhite flex items-center uppercase font-semibold'>
                    Go to Library
                  </span>
                </Button>
              </Link>
            </div>
          </ShouldRender>
        </span>

        <div className='flex flex-wrap gap-3  md:justify-end '>
          <Link
            className=''
            href={`/my-orders/order-details/${data.orderId}${isInAppPurchase ? '?inApp=true' : ''
              }`}
          >
            <Button className='border flex justify-center hover:text-white border-[#363636] w-full h-9 '>
              <span className='px-4 font-semibold text-sm  -translate-y-0.5 md:translate-y-0 '>
                VIEW
              </span>
            </Button>
          </Link>

          <ShouldRender
            check={data?.payment_status?.toLowerCase() == 'un-paid' && data?.paymentType?.toLowerCase() == 'stripe'}
          >
            <Button
              size='middle'
              type='default'
              onClick={() => {
                const eventParams: EventDetails = {
                  event_name: 'retry_payment',
                  source: 'global',
                  source_type: 'orders',
                  source_id: data?.orderId?.toString(),
                  sub_source: null,
                  sub_source_id: null,
                  unit: data?.currency_code || 'INR',
                  currency: data?.currency_code || 'INR',
                  value: Number(data?.grand_total || 0),
                  meta: {
                    click_source: 'my_orders',
                    payment_mode: 'stripe',
                    currency: data?.currency_code || 'INR',
                    country: data?.shipping_address?.country || '',
                    state: data?.shipping_address?.state || '',
                    city: data?.shipping_address?.city || '',
                    postal_code: data?.shipping_address?.postal_code || '',
                  },
                };
                logEvent({
                  eventDetails: eventParams,
                  userData: userData,
                  pathname: router.pathname,
                });
                if (data?.payment_details?.payment_url) {
                  window.location.href = data.payment_details.payment_url;
                }
              }}
              className='bg-fadedWhite  h-9 w-auto'
            >
              <span
                style={{ display: 'flex' }}
                className='md:px-4 text-black text-sm  font-medium gap-x-2 justify-center flex items-center whitespace-nowrap'
              >
                Retry Payment
              </span>
            </Button>
          </ShouldRender>

          <ShouldRender
            check={
              (data.payment_status === 'Un-Paid' ||
                data.payment_status?.toLowerCase() === 'failed') &&
              data?.paymentType?.toLowerCase() === 'razorpay' &&
              data.delivery_status?.toLowerCase() !== 'refunded'
            }
          >
            <ShouldRender
              check={data.delivery_status?.toLowerCase() !== 'failed'}
            >
              <Button
                size='middle'
                type='default'
                onClick={() => {
                  const eventParams: EventDetails = {
                    event_name: 'retry_payment',
                    source: 'global',
                    source_type: 'orders',
                    source_id: data?.orderId?.toString(),
                    sub_source: null,
                    sub_source_id: null,
                    unit: data?.currency_code || 'INR',
                    currency: data?.currency_code || 'INR',
                    value: Number(data?.grand_total || 0),
                    meta: {
                      click_source: 'my_orders',
                      payment_mode: 'prepaid',
                      currency: data?.currency_code || 'INR',
                      country: data?.shipping_address?.country || '',
                      state: data?.shipping_address?.state || '',
                      city: data?.shipping_address?.city || '',
                      postal_code: data?.shipping_address?.postal_code || '',
                    },
                  };
                  logEvent({
                    eventDetails: eventParams,
                    userData: userData,
                    pathname: router.pathname,
                  });
                  handlePlaceOrder();
                }}
                className='bg-fadedWhite  h-9 w-auto'
              >
                <span
                  style={{ display: 'flex' }}
                  className='md:px-4 text-black text-sm  font-medium gap-x-2 justify-center flex items-center whitespace-nowrap'
                >
                  Retry Payment
                </span>
              </Button>
            </ShouldRender>
            <ShouldRender check={!hasDigitalOrPreorder && containCodFalse}>
              <Button
                size='middle'
                type='default'
                disabled={
                  convertCodLoading ||
                  (Number(data?.grand_total) || 0) > cod_limit
                }
                onClick={() => {
                  convertToCod();
                }}
                className='bg-fadedWhite  h-9 w-auto'
              >
                <span className='flex text-sm text-black font-medium  gap-x-2 md:px-4 py-0.5 whitespace-nowrap'>
                  {!convertCodLoading ? 'Complete with COD' : 'Completing'}
                  {convertCodLoading && <DotLoader />}
                </span>
              </Button>
            </ShouldRender>
          </ShouldRender>
          {/* 
          <ShouldRender
            check={
              (!isInAppPurchase &&
                data.payment_status !== 'failed' &&
                (!isAllDigital ||
                  (isAllDigital &&
                    moment(data.orderDate).isSameOrAfter(
                      moment().subtract(1, 'days'),
                      'day',
                    ))) &&
                !(
                  data.paymentType === 'Razorpay' &&
                  data.payment_status === 'Un-Paid'
                )) ||
              (data.paymentType === 'Razorpay' &&
                data.payment_status === 'Paid')
            }
          >
            <ShouldRender check={canShowCancelButton}>
              {data?.order_cancel_request ? (
                <Tooltip title='Your cancellation request is being processed. You will receive an update shortly.'>
                  {cancelButton}
                </Tooltip>
              ) : (
                cancelButton
              )}
            </ShouldRender>
          </ShouldRender> */}
          <RenderIf condition={data?.shipping_details && data?.shipping_details?.awb_id}>
            <Button
              size='middle'
              type='default'
              className='bg-fadedWhite h-9'
              disabled={!getTrackOrderLink(data?.shipping_details)}
              onClick={() => {
                const link = getTrackOrderLink(data?.shipping_details);
                if (link) {
                  window.open(link, '_blank');
                }
              }}
            >
              <span className={clsx('md:px-4 text-sm font-semibold', !getTrackOrderLink(data?.shipping_details) ? 'text-gray-400' : 'text-black')}>
                TRACK ORDER
              </span>
            </Button>
          </RenderIf>
          <ShouldRender
            check={
              !(
                (data.payment_status === 'Un-Paid' ||
                  data.payment_status === 'failed') &&
                ['Stripe', 'Razorpay'].includes(data?.paymentType || '')
              )
            }
          >
            <ShouldRender check={data.payment_status === 'Paid' && !screens.md}>
              <div>
                <Link className='w-full' href='/library'>
                  <Button
                    size='middle'
                    className='border w-full border-[#363636] hover:text-white h-9'
                  >
                    <span className='px-1 md:px-4 w-full text-sm   text-fadedWhite flex items-center uppercase font-semibold'>
                      Go to Library
                    </span>
                  </Button>
                </Link>
              </div>
            </ShouldRender>
          </ShouldRender>
        </div>
      </div>
      <div className='flex text-xs md:text-sm items-start lg:items-center justify-center gap-x-2 pt-4 md:pt-7'>
        <span
          onClick={() => !isRotating && handleSyncOrder()}
          className='flex justify-center items-center gap-x-2  cursor-pointer'
        >
          <motion.div
            className='cursor-pointer '
            animate={isRotating ? { rotate: [0, 360] } : {}}
            transition={{
              repeat: isRotating ? Infinity : 0,
              duration: 2,
              ease: 'linear',
            }}
            style={{ originX: 0.5, originY: 0.5 }}
          >
            <RetryIcon width={screens.md ? '20' : '16'} />
          </motion.div>
          <p className='text-[#6199FF] hidden lg:block underline'>
            {' '}
            Click here
          </p>
        </span>
        <div className='flex'>
          <p>
            <span
              onClick={() => !isRotating && handleSyncOrder()}
              className='inline lg:hidden text-[#6199FF] underline font-semibold pr-1'
            >
              {' '}
              Click here
            </span>

            <span className='inline'>
              to Refresh payment status if the amount has been debited but not
              reflected as paid.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyOrderCard;
